import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PickerProvider, type MediaPickOptions, type PickerApi } from '@/lib/forms/PickerContext';
import type { MediaAsset } from '@/lib/api/types';
import { Dialog } from '@/lib/ui/Dialog';
import { mediaApi, uploadIssue } from './api';
import { hasErrorCode } from '@/features/shared/governance';
import { isAppError } from '@/lib/api/errors';
import { formatBytes } from '@/lib/format';
import { keys } from '@/lib/query/keys';
import { MediaBrowser } from './MediaBrowser';
import { thumbUrl } from './derivatives';
import { EntityPickerModal } from '@/features/content/EntityPickerModal';

/**
 * Hosts the media + entity picker modals and exposes imperative pick() resolvers to the
 * schema-form widgets via PickerProvider (plan §4.7). Decouples forms from the media module.
 */
export function MediaPickerHost({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  // `null` = closed; otherwise the options the current pick was opened with.
  const [mediaOpen, setMediaOpen] = useState<MediaPickOptions | null>(null);
  const [entityOpen, setEntityOpen] = useState<{ collection: string } | null>(null);
  const mediaResolver = useRef<((asset: MediaAsset | null) => void) | null>(null);
  const entityResolver = useRef<((id: string | null) => void) | null>(null);

  const pickMediaAsset = useCallback<PickerApi['pickMediaAsset']>((opts) => {
    // A second open supersedes a pending one; settle the old promise so no caller hangs.
    mediaResolver.current?.(null);
    setMediaOpen(opts ?? {});
    return new Promise<MediaAsset | null>((resolve) => {
      mediaResolver.current = resolve;
    });
  }, []);

  const pickMedia = useCallback<PickerApi['pickMedia']>(
    async (opts) => (await pickMediaAsset(opts))?.id ?? null,
    [pickMediaAsset],
  );

  const pickEntity = useCallback<PickerApi['pickEntity']>((collection) => {
    setEntityOpen({ collection });
    return new Promise<string | null>((resolve) => {
      entityResolver.current = resolve;
    });
  }, []);

  const queryClient = useQueryClient();
  const mediaDimensions = useCallback<PickerApi['mediaDimensions']>(
    (id) => {
      const asset = queryClient.getQueryData<MediaAsset>(keys.media.detail(id));
      if (!asset || typeof asset.width !== 'number' || typeof asset.height !== 'number') return null;
      return { width: asset.width, height: asset.height };
    },
    [queryClient],
  );

  const checkUpload = useCallback<PickerApi['checkUpload']>(
    (file) => {
      const issue = uploadIssue(file);
      if (!issue) return null;
      return issue.kind === 'type'
        ? t('media.unsupportedType', { ext: issue.ext })
        : t('media.exceedsLimit', { limit: formatBytes(issue.limit) });
    },
    [t],
  );

  const uploadImage = useCallback<PickerApi['uploadImage']>(
    async (file, alt) => {
      try {
        // alt.en is required by the server for an image; alt.ar is optional (publishing warns).
        const asset = await mediaApi.upload({ file, alt: { en: alt.en.trim(), ar: alt.ar.trim() } });
        void queryClient.invalidateQueries({ queryKey: keys.media.all() });
        // Seed the detail cache so the new row's thumbnail and size check need no second request.
        queryClient.setQueryData(keys.media.detail(asset.id), asset);
        return asset.id;
      } catch (e) {
        if (hasErrorCode(e, 'unsafe_svg')) throw new Error(t('media.unsafeSvgDetail'));
        throw new Error(isAppError(e) && e.message ? e.message : t('forms.gallery.failed'));
      }
    },
    [queryClient, t],
  );

  const api: PickerApi = {
    pickMedia,
    pickMediaAsset,
    pickEntity,
    mediaDimensions,
    checkUpload,
    uploadImage,
    MediaPreview: ({ id }) => <MediaPreview id={id} />,
  };

  const closeMedia = (asset: MediaAsset | null) => {
    mediaResolver.current?.(asset);
    mediaResolver.current = null;
    setMediaOpen(null);
  };
  const closeEntity = (id: string | null) => {
    entityResolver.current?.(id);
    entityResolver.current = null;
    setEntityOpen(null);
  };

  return (
    <PickerProvider value={api}>
      {children}
      <Dialog
        open={mediaOpen !== null}
        onOpenChange={(o) => !o && closeMedia(null)}
        title={t('media.chooseMedia')}
        size="xl"
      >
        <MediaBrowser scope="modal" canUpload kind={mediaOpen?.kind} onSelect={(asset) => closeMedia(asset)} />
      </Dialog>
      {entityOpen && (
        <EntityPickerModal
          collection={entityOpen.collection}
          open
          onClose={(id) => closeEntity(id)}
        />
      )}
    </PickerProvider>
  );
}

function MediaPreview({ id }: { id: string | null }) {
  const query = useQuery({
    queryKey: keys.media.detail(id ?? 'none'),
    queryFn: () => mediaApi.get(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
  if (!id) return <div className="h-[44px] w-[44px] rounded-sm bg-canvas-deep-sand" aria-hidden />;
  const asset = query.data;
  // SVG drawings render through <img> too (the browser rasterises them; no inline markup).
  if ((asset?.kind === 'image' || asset?.kind === 'svg') && !asset.is_restricted) {
    return (
      <img
        src={thumbUrl(asset)}
        alt={asset.alt?.en ?? ''}
        className="h-[44px] w-[44px] rounded-sm object-cover"
      />
    );
  }
  return (
    <div className="flex h-[44px] w-[44px] items-center justify-center rounded-sm bg-canvas-deep-sand text-ink-mute">
      {asset ? '📄' : '…'}
    </div>
  );
}
